import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { SayHiNudgeDialog } from '~/components/network/SayHiNudgeDialog';
import { useAnalytics } from '~/context/analytics';
import { useMessagingDockOptional } from '~/context/messagingDock';
import { stashComposeDraft } from '~/utils/composeDraftHandoff';

type SayHiNudgeRole = 'acceptor' | 'requester';

export type SayHiNudgeTarget = {
  recipientAsfId: string;
  displayName: string;
  connectionId?: string;
  role: SayHiNudgeRole;
  source: string;
};

/**
 * After a connection is accepted, nudge both parties to send a first DM.
 * Opens the messaging dock compose (or `/messages/new`) with a chosen opener.
 */
export function useSayHiNudge() {
  const navigate = useNavigate();
  const messagingDock = useMessagingDockOptional();
  const {
    amplitude: { track },
  } = useAnalytics();
  const [target, setTarget] = useState<SayHiNudgeTarget | null>(null);
  const targetRef = useRef<SayHiNudgeTarget | null>(null);

  const show = useCallback(
    (next: SayHiNudgeTarget) => {
      track('connection_say_hi_nudge_shown', {
        connection_id: next.connectionId,
        recipient_user_id: next.recipientAsfId,
        role: next.role,
        source: next.source,
      });
      targetRef.current = next;
      setTarget(next);
    },
    [track]
  );

  const dismiss = useCallback(() => {
    const current = targetRef.current;
    if (!current) return;
    track('connection_say_hi_nudge_dismissed', {
      connection_id: current.connectionId,
      recipient_user_id: current.recipientAsfId,
      role: current.role,
      source: current.source,
    });
    targetRef.current = null;
    setTarget(null);
  }, [track]);

  const continueWith = useCallback(
    (body: string) => {
      const current = targetRef.current;
      if (!current) return;
      track('connection_say_hi_nudge_compose_opened', {
        connection_id: current.connectionId,
        recipient_user_id: current.recipientAsfId,
        role: current.role,
        source: current.source,
        body_length: body.length,
      });
      // Clear before opening compose so a Radix onOpenChange(false) no-ops dismiss.
      targetRef.current = null;
      setTarget(null);
      if (messagingDock) {
        messagingDock.openCompose({
          recipientAsfId: current.recipientAsfId,
          recipient: {
            asfId: current.recipientAsfId,
            displayName: current.displayName,
          },
          body,
          source: 'say_hi_nudge',
        });
      } else {
        // Never put draft text in the URL — analytics / history / referrers.
        stashComposeDraft(body);
        const params = new URLSearchParams({
          to: current.recipientAsfId,
          name: current.displayName,
        });
        navigate(`/messages/new?${params.toString()}`);
      }
    },
    [messagingDock, navigate, track]
  );

  const dialog = (
    <SayHiNudgeDialog
      open={!!target}
      displayName={target?.displayName ?? ''}
      onOpenChange={(open) => {
        if (!open) dismiss();
      }}
      onContinue={continueWith}
      onDismiss={dismiss}
    />
  );

  return { show, dialog };
}
